import { useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ApiError } from '@/lib/api'

interface ConfirmActionProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: ReactNode
  confirmLabel: string
  /** Styles the confirm button as destructive. */
  destructive?: boolean
  /**
   * Adds a note field. Every queue action in the contract accepts an optional
   * `note`, which is what makes a rejection legible in the audit log later.
   */
  note?: { label?: string; placeholder?: string; required?: boolean }
  /** Resolves on success; reject to surface the failure in the dialog. */
  onConfirm: (note: string) => Promise<unknown>
  /**
   * Called after a `2001`, once the operator dismisses it. The list this dialog
   * was opened from is stale, so the caller refetches.
   */
  onConflict?: () => void
}

/**
 * The single confirm step behind every queue decision — verify, reject, approve,
 * revoke, delete. Centralised mostly for one reason: `2001` handling. Two
 * operators working the same queue is the normal case, not the edge case, and
 * without this each screen would invent its own wording and its own wrong
 * retry behaviour.
 */
export function ConfirmAction({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive = false,
  note,
  onConfirm,
  onConflict,
}: ConfirmActionProps) {
  const [value, setValue] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [conflict, setConflict] = useState(false)

  function close(next: boolean) {
    if (pending) return
    if (!next) {
      // A conflict means the caller's data is stale whether or not they read the
      // message, so tell it on the way out.
      if (conflict) onConflict?.()
      setValue('')
      setError(null)
      setConflict(false)
    }
    onOpenChange(next)
  }

  const noteMissing = Boolean(note?.required) && value.trim().length === 0

  async function submit() {
    if (noteMissing) {
      setError('A note is required.')
      return
    }

    setPending(true)
    setError(null)
    try {
      await onConfirm(value.trim())
      setValue('')
      onOpenChange(false)
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.isInvalidState) {
        setConflict(true)
      } else {
        setError(thrown instanceof Error ? thrown.message : 'That did not work.')
      }
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{conflict ? 'Already actioned' : title}</DialogTitle>
          <DialogDescription>
            {conflict
              ? 'Someone else actioned this while the screen was open, so nothing was changed. The list will reload — take another look before deciding.'
              : description}
          </DialogDescription>
        </DialogHeader>

        {!conflict && note && (
          <div className="space-y-2">
            <Label htmlFor="confirm-note">
              {note.label ?? 'Note'}
              {!note.required && <span className="text-faint"> (optional)</span>}
            </Label>
            <Textarea
              id="confirm-note"
              rows={3}
              placeholder={note.placeholder}
              value={value}
              onChange={(event) => {
                setValue(event.target.value)
                if (error) setError(null)
              }}
            />
          </div>
        )}

        {error && <p className="text-xs text-destructive">{error}</p>}

        <DialogFooter>
          {conflict ? (
            <Button onClick={() => close(false)}>Reload the list</Button>
          ) : (
            <>
              <Button variant="secondary" disabled={pending} onClick={() => close(false)}>
                Cancel
              </Button>
              <Button
                variant={destructive ? 'destructive' : 'default'}
                disabled={pending}
                onClick={() => void submit()}
              >
                {pending ? 'Working…' : confirmLabel}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
